import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, MapPin, Star, Search, Pencil, ExternalLink, Ban, Check, Save, Trash2, Upload, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { turkeyData, provinces } from "@/data/turkeyData";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import ImageCropper from "@/components/ImageCropper";
import GalleryDropzone from "@/components/GalleryDropzone";
import { Checkbox } from "@/components/ui/checkbox";

export default function AdminSchools() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", province: "", district: "", short_intro: "" });
  const [newFacility, setNewFacility] = useState("");
  
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    setUploading(true);
    try {
      const res = await api.post(`${V1}/admin/schools/import-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${res.data.imported_count} okul başarıyla eklendi`);
      if (res.data.errors && res.data.errors.length > 0) {
        console.error("CSV Import Hataları:", res.data.errors);
        toast.warning(`${res.data.errors.length} satırda hata oluştu (Konsolu kontrol edin)`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV yüklenirken hata oluştu");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const checkMissingFields = (school) => {
    const missing = [];
    if (!school.logo_url) missing.push("Logo");
    if (!school.cover_url) missing.push("Kapak Fotoğrafı");
    if (!school.short_intro) missing.push("Kısa Tanıtım");
    if (!school.phone) missing.push("Telefon");
    if (!school.email) missing.push("E-posta");
    if (!school.address) missing.push("Adres");
    if (!school.website) missing.push("Web Sitesi");
    return missing;
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v, ...(k === "province" ? { district: "" } : {}) }));

  const [editing, setEditing] = useState(null); // school being edited
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const setEdit = (k, v) => setEditForm(f => ({ ...f, [k]: v, ...(k === "province" ? { district: "" } : {}) }));

  const load = () => api.get(`${V1}/schools?limit=500&admin=true`).then((r) => setItems(r.data.items || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post(`${V1}/schools`, form);
      toast.success("Okul oluşturuldu");
      setOpen(false); setForm({ name: "", province: "", district: "", short_intro: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
  };

  const toggleFeatured = async (s) => {
    await api.patch(`${V1}/schools/${s.id}`, { featured: !s.featured });
    load();
  };
  const setStatus = async (s, status) => {
    await api.patch(`${V1}/admin/schools/${s.id}/status`, { status });
    toast.success(status === "suspended" ? "Askıya alındı" : "Onaylandı");
    load();
  };

  const deleteSchool = async (s) => {
    if (!window.confirm(`${s.name} okulunu tamamen silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`${V1}/schools/${s.id}`);
      toast.success("Okul silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Okul silinemedi");
    }
  };


  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      name: s.name || "",
      province: s.province || "",
      district: s.district || "",
      address: s.address || "",
      phone: s.phone || "",
      email: s.email || "",
      website: s.website || "",
      short_intro: s.short_intro || "",
      long_description: s.long_description || "",
      cover_url: s.cover_url || "",
      logo_url: s.logo_url || "",
      brochure_url: s.brochure_url || "",
      gallery: s.gallery || [],
      education_levels: s.education_levels || [],
      facilities: s.facilities || [],
      tuition_min: s.tuition_min || "",
      boarding: !!s.boarding,
      foreign_language: !!s.foreign_language,
      featured: !!s.featured,
      is_published: s.is_published !== false,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        province: editForm.province,
        district: editForm.district,
        address: editForm.address,
        phone: editForm.phone,
        email: editForm.email,
        website: editForm.website,
        short_intro: editForm.short_intro,
        long_description: editForm.long_description,
        cover_url: editForm.cover_url,
        logo_url: editForm.logo_url,
        brochure_url: editForm.brochure_url,
        gallery: editForm.gallery,
        education_levels: editForm.education_levels,
        facilities: editForm.facilities,
        tuition_min: editForm.tuition_min ? parseInt(editForm.tuition_min) : null,
        boarding: editForm.boarding,
        foreign_language: editForm.foreign_language,
        featured: editForm.featured,
        is_published: editForm.is_published,
      };
      await api.patch(`${V1}/schools/${editing.id}`, payload);
      toast.success("Okul güncellendi");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  const filtered = items.filter((s) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (!q) return true;
    const t = q.toLowerCase();
    return (s.name || "").toLowerCase().includes(t)
      || (s.province || "").toLowerCase().includes(t)
      || (s.district || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6" data-testid="admin-schools-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Yönetim</div>
          <h1 className="font-display text-3xl font-bold">Okullar</h1>
          <p className="text-muted-foreground text-sm mt-1">{filtered.length} / {items.length} okul</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" accept=".csv" className="hidden" ref={fileInputRef} onChange={handleCSVUpload} />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            <Upload className="h-4 w-4 mr-1.5" />
            {uploading ? "Yükleniyor..." : "İçe Aktar (CSV)"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-school-btn"><Plus className="h-4 w-4" /> Yeni Okul</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Okul Oluştur</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Okul Adı</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="school-form-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>İl</Label>
                  <Select value={form.province} onValueChange={(v) => set("province", v)}>
                    <SelectTrigger data-testid="school-form-province"><SelectValue placeholder="İl Seçiniz" /></SelectTrigger>
                    <SelectContent>
                      {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>İlçe</Label>
                  <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.province}>
                    <SelectTrigger data-testid="school-form-district"><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
                    <SelectContent>
                      {form.province && turkeyData[form.province] ? turkeyData[form.province].map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      )) : null}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Kısa Tanıtım</Label><Input value={form.short_intro} onChange={(e) => setForm({ ...form, short_intro: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button><Button onClick={create} data-testid="save-school-btn">Kaydet</Button></DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Okul, il veya ilçe ara..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-school-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 border border-border rounded-md bg-background" data-testid="admin-school-status-filter">
          <option value="all">Tüm durumlar</option>
          <option value="approved">Onaylı</option>
          <option value="pending">Beklemede</option>
          <option value="suspended">Askıda</option>
        </select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((s) => {
          const missingFields = checkMissingFields(s);
          const isComplete = missingFields.length === 0;
          return (
          <div key={s.id} className={`p-5 rounded-xl border flex gap-4 ${isComplete ? 'border-border bg-card' : 'border-destructive bg-destructive/5'}`} data-testid={`admin-school-row-${s.id}`}>
            {s.cover_url ? (
              <img src={s.cover_url} alt="" className="h-20 w-24 object-cover rounded shrink-0" />
            ) : (
              <div className="h-20 w-24 bg-muted rounded grid place-content-center text-xs text-muted-foreground shrink-0">Görsel yok</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.province} / {s.district}</div>
              <div className="font-display font-bold truncate">{s.name}</div>
              {!isComplete && (
                <div className="mt-1 text-[11px] text-destructive flex items-start gap-1 leading-tight">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>Eksikler: {missingFields.join(", ")}</span>
                </div>
              )}
              <div className="mt-2 flex gap-1.5 items-center flex-wrap">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-medium ${s.status === "approved" ? "bg-success/10 text-success" : s.status === "suspended" ? "bg-destructive/10 text-destructive" : "bg-muted"}`}>
                  {s.status === "approved" ? "Onaylı" : s.status === "suspended" ? "Askıda" : "Beklemede"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => toggleFeatured(s)} title="Öne çıkar" data-testid={`toggle-featured-${s.id}`}>
                  <Star className={`h-3.5 w-3.5 ${s.featured ? "text-accent fill-accent" : ""}`} />
                </Button>
                <Button size="sm" variant="outline" asChild data-testid={`view-school-${s.id}`}>
                  <Link to={`/okul/${s.slug}`} target="_blank"><ExternalLink className="h-3.5 w-3.5" /> Detay</Link>
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`edit-school-${s.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Düzenle
                </Button>
                <Button size="sm" variant="outline" onClick={() => setStatus(s, s.status === "approved" ? "suspended" : "approved")} data-testid={`toggle-status-${s.id}`}>
                  {s.status === "approved" ? <><Ban className="h-3.5 w-3.5" /> Askıya Al</> : <><Check className="h-3.5 w-3.5" /> Onayla</>}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteSchool(s)} data-testid={`delete-school-${s.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Sil
                </Button>
              </div>
            </div>
          </div>
        );
        })}
        {filtered.length === 0 && <div className="col-span-2 p-10 text-center text-muted-foreground">Filtreye uygun okul yok</div>}
      </div>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="edit-school-dialog">
          <DialogHeader>
            <DialogTitle>Okul Bilgilerini Düzenle</DialogTitle>
            <DialogDescription>{editing?.name}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Label>Okul Adı</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" /></div>
              <div>
                <Label>İl</Label>
                <Select value={editForm.province} onValueChange={(v) => setEdit("province", v)}>
                  <SelectTrigger data-testid="edit-province"><SelectValue placeholder="İl Seçiniz" /></SelectTrigger>
                  <SelectContent>
                    {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>İlçe</Label>
                <Select value={editForm.district} onValueChange={(v) => setEdit("district", v)} disabled={!editForm.province}>
                  <SelectTrigger data-testid="edit-district"><SelectValue placeholder="İlçe Seçiniz" /></SelectTrigger>
                  <SelectContent>
                    {editForm.province && turkeyData[editForm.province] ? turkeyData[editForm.province].map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    )) : null}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label>Adres</Label><Textarea rows={2} value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
              <div><Label>E-posta</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Website</Label><Input value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} /></div>
              <div className="md:col-span-2"><Label>Kısa Tanıtım</Label><Textarea rows={2} value={editForm.short_intro} onChange={(e) => setEditForm({ ...editForm, short_intro: e.target.value })} data-testid="edit-short-intro" /></div>
              <div className="md:col-span-2">
                <Label>Uzun Açıklama (Zengin Metin)</Label>
                <div className="prose max-w-none mt-1 border rounded-md">
                  <CKEditor
                    editor={ClassicEditor}
                    data={editForm.long_description || ""}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setEditForm({ ...editForm, long_description: data });
                    }}
                  />
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Kapak Görseli</Label>
                <ImageCropper value={editForm.cover_url} onChange={(url) => setEditForm({...editForm, cover_url: url})} type="cover" aspect={1024/700} schoolId={editing.id} width={1024} height={700} />
              </div>
              <div>
                <Label className="mb-2 block">Logo</Label>
                <ImageCropper value={editForm.logo_url} onChange={(url) => setEditForm({...editForm, logo_url: url})} type="logo" aspect={1/1} schoolId={editing.id} width={400} height={400} />
              </div>
              <div className="md:col-span-2"><Label>Broşür PDF URL</Label><Input value={editForm.brochure_url} onChange={(e) => setEditForm({ ...editForm, brochure_url: e.target.value })} /></div>
              <div className="md:col-span-2">
                <Label className="mb-2 block">Galeri</Label>
                <GalleryDropzone value={editForm.gallery} onChange={(urls) => setEditForm({...editForm, gallery: urls})} schoolId={editing.id} />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-3 block">Eğitim Kademeleri</Label>
                <div className="flex flex-wrap gap-4">
                  {["Kreş", "Anaokulu", "İlkokul", "Ortaokul", "Lise"].map(level => (
                    <label key={level} className="flex items-center gap-2 text-sm cursor-pointer border p-2 rounded-md hover:bg-muted/50">
                      <Checkbox 
                        checked={(editForm.education_levels || []).includes(level)}
                        onCheckedChange={(checked) => {
                          const current = editForm.education_levels || [];
                          if (checked) {
                            setEditForm({...editForm, education_levels: [...current, level]});
                          } else {
                            setEditForm({...editForm, education_levels: current.filter(l => l !== level)});
                          }
                        }}
                      />
                      {level}
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="mb-3 block">Olanaklar</Label>
                <div className="flex flex-wrap gap-4 mb-3">
                  {["Laboratuvar", "Spor Salonu", "Kütüphane", "Yemekhane", "Havuz", "Konferans Salonu"].map(fac => {
                    const has = (editForm.facilities || []).includes(fac);
                    return (
                      <label key={fac} className="flex items-center gap-2 text-sm cursor-pointer border p-2 rounded-md hover:bg-muted/50">
                        <Checkbox 
                          checked={has}
                          onCheckedChange={(checked) => {
                            const current = editForm.facilities || [];
                            if (checked) setEditForm({...editForm, facilities: [...current, fac]});
                            else setEditForm({...editForm, facilities: current.filter(f => f !== fac)});
                          }}
                        />
                        {fac}
                      </label>
                    );
                  })}
                  {(editForm.facilities || []).filter(f => !["Laboratuvar", "Spor Salonu", "Kütüphane", "Yemekhane", "Havuz", "Konferans Salonu"].includes(f)).map(fac => (
                     <label key={fac} className="flex items-center gap-2 text-sm cursor-pointer border p-2 rounded-md hover:bg-muted/50">
                        <Checkbox 
                          checked={true}
                          onCheckedChange={(checked) => {
                            if (!checked) setEditForm({...editForm, facilities: editForm.facilities.filter(f => f !== fac)});
                          }}
                        />
                        {fac}
                      </label>
                  ))}
                </div>
                <div className="flex gap-2 max-w-sm">
                  <Input 
                    value={newFacility} 
                    onChange={e => setNewFacility(e.target.value)} 
                    placeholder="Yeni olanak ekle..." 
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const val = newFacility.trim();
                        if (val && !(editForm.facilities || []).includes(val)) {
                          setEditForm({...editForm, facilities: [...(editForm.facilities || []), val]});
                          setNewFacility("");
                        }
                      }
                    }}
                  />
                  <Button type="button" variant="secondary" onClick={() => {
                    const val = newFacility.trim();
                    if (val && !(editForm.facilities || []).includes(val)) {
                      setEditForm({...editForm, facilities: [...(editForm.facilities || []), val]});
                      setNewFacility("");
                    }
                  }}>Ekle</Button>
                </div>
              </div>
              <div><Label>Başlangıç Ücret (₺/yıl)</Label><Input type="number" value={editForm.tuition_min} onChange={(e) => setEditForm({ ...editForm, tuition_min: e.target.value })} /></div>
              <div className="flex items-center gap-6 pt-6">
                <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.boarding} onCheckedChange={(v) => setEditForm({ ...editForm, boarding: v })} /> Yatılı</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.foreign_language} onCheckedChange={(v) => setEditForm({ ...editForm, foreign_language: v })} /> Yabancı Dil</label>
              </div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.featured} onCheckedChange={(v) => setEditForm({ ...editForm, featured: v })} /> Öne Çıkar</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={editForm.is_published} onCheckedChange={(v) => setEditForm({ ...editForm, is_published: v })} /> Yayında</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Vazgeç</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-school-edit-btn"><Save className="h-4 w-4" /> Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
